import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns as getCampaigns,
  getDashboardLanguage  as getLanguageData,
  getDashboardTransfers as getTransfersData,
} from "../../services/pbi/pbiservices";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const PURPLE = "#7F77DD";

const GRAN_OPTIONS = ["Day", "Week", "Month"];

// ── Date range presets ─────────────────────────────────────────────
const RANGE_OPTIONS = [
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

function getDateRange(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "7D":
      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":
      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":
      start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":
      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":
      start = thisMonday; end = now; break;
    case "CW+Last":
      start = lastMonday; end = now; break;
    case "This Mo":
      start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":
      start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":
      start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:
      start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  const toLocal = (d) => {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  };
  return {
    startDate: toLocal(start),
    endDate:   toLocal(end),
  };
}

// Previous period = same duration as current, ending the day before current starts.
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start  = parse(startDate);
  const end    = parse(endDate);
  const durMs  = end.getTime() - start.getTime();
  const dayMs  = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Helpers ────────────────────────────────────────────────────────
function fmt(n, dec = 0) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toFixed(dec);
}

function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  // new Date("2026-04-16") parses as UTC midnight → shifts back a day
  // in negative-offset timezones. Slashes force local-time parsing.
  const d = new Date(s.replace(/-/g, "/"));
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

// Build a delta object from current/prev numeric values.
// Build a delta object from current/prev numeric values.
//   text: "+5.2% vs 1,234" — pct change + actual prev value formatted.
// `fmtVal` formats the prev value; defaults to comma-formatted integer.
// Pass a custom formatter for percentages, currency, etc.
function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

// Shared formatter for percentage-typed prev values.
const pctFmt = v => `${fmt(v, 2)}%`;

// Aggregation helpers — shared by current and prev period.
function aggregateLanguage(rows) {
  const total   = (rows || []).reduce((a,b) => a + safeNum(b.total_count),   0);
  const english = (rows || []).reduce((a,b) => a + safeNum(b.english_count), 0);
  const spanish = (rows || []).reduce((a,b) => a + safeNum(b.spanish_count), 0);
  return {
    total, english, spanish,
    englishPct: total > 0 ? english / total * 100 : null,
    spanishPct: total > 0 ? spanish / total * 100 : null,
  };
}

function aggregateTransfers(rows) {
  const engTotal = (rows || []).reduce((a,b) => a + safeNum(b.english_total), 0);
  const engYes   = (rows || []).reduce((a,b) => a + safeNum(b.english_yes),   0);
  const spaTotal = (rows || []).reduce((a,b) => a + safeNum(b.spanish_total), 0);
  const spaYes   = (rows || []).reduce((a,b) => a + safeNum(b.spanish_yes),   0);
  return {
    engRecords: engTotal,
    spaRecords: spaTotal,
    engRate: engTotal > 0 ? engYes / engTotal * 100 : null,
    spaRate: spaTotal > 0 ? spaYes / spaTotal * 100 : null,
  };
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{ background: "#f7f7f7", borderRadius: 2, p: "10px 14px", minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2 }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none", border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition: "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
export default function LanguageDashboard({ channel = "texts" }) {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);
  const [language,        setLanguage]      = useState([]);
  const [transfers,       setTransfers]     = useState([]);
  const [prevLanguage,    setPrevLanguage]  = useState([]);
  const [prevTransfers,   setPrevTransfers] = useState([]);
  const [loading,         setLoading]       = useState(true);
  const [error,           setError]         = useState("");

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [rangePreset, campaign, gran, channel]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [params.startDate, params.endDate, campaign, gran, channel]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [l, tr, pl, ptr] = await Promise.all([
        getLanguageData(params),
        getTransfersData(params),
        // Prev period: failures non-fatal — KPIs just won't show deltas.
        getLanguageData(prevParams).catch(() => []),
        getTransfersData(prevParams).catch(() => []),
      ]);
      setLanguage(l       || []);
      setTransfers(tr     || []);
      setPrevLanguage(pl  || []);
      setPrevTransfers(ptr || []);
    } catch (e) {
      setError("Failed to load language data"); console.error(e);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── KPIs (with prev-period deltas) ─────────────────────────────
  // Counts/percentages: more activity = good (no clear bad direction for splits).
  // Transfer rates: lower = better (more autonomous handling).
  const langKpis = useMemo(() => {
    const cur  = aggregateLanguage(language);
    const prev = aggregateLanguage(prevLanguage);
    return {
      total:      cur.total,
      english:    cur.english,
      englishPct: cur.englishPct == null ? "—" : `${fmt(cur.englishPct, 2)}%`,
      spanishPct: cur.spanishPct == null ? "—" : `${fmt(cur.spanishPct, 2)}%`,
      totalDelta:      buildDelta(cur.total,      prev.total,      { goodWhenUp: true }),
      englishDelta:    buildDelta(cur.english,    prev.english,    { goodWhenUp: true }),
      englishPctDelta: buildDelta(cur.englishPct, prev.englishPct, { goodWhenUp: true, fmtVal: pctFmt }),
      spanishPctDelta: buildDelta(cur.spanishPct, prev.spanishPct, { goodWhenUp: true, fmtVal: pctFmt }),
    };
  }, [language, prevLanguage]);

  const transferKpis = useMemo(() => {
    const cur  = aggregateTransfers(transfers);
    const prev = aggregateTransfers(prevTransfers);
    return {
      engRecords: cur.engRecords,
      spaRecords: cur.spaRecords,
      engRate: cur.engRate == null ? "—" : `${fmt(cur.engRate, 2)}%`,
      spaRate: cur.spaRate == null ? "—" : `${fmt(cur.spaRate, 2)}%`,
      engRecordsDelta: buildDelta(cur.engRecords, prev.engRecords, { goodWhenUp: true  }),
      engRateDelta:    buildDelta(cur.engRate,    prev.engRate,    { goodWhenUp: false, fmtVal: pctFmt }),
      spaRecordsDelta: buildDelta(cur.spaRecords, prev.spaRecords, { goodWhenUp: true  }),
      spaRateDelta:    buildDelta(cur.spaRate,    prev.spaRate,    { goodWhenUp: false, fmtVal: pctFmt }),
    };
  }, [transfers, prevTransfers]);

  const langChartData = useMemo(() => language.map(r => ({
    date:    fmtDate(r.summary_date),
    English: safeNum(r.english_count),
    Spanish: safeNum(r.spanish_count),
  })), [language]);

  const transferChartData = useMemo(() => transfers.map(r => ({
    date:           fmtDate(r.summary_date),
    "Eng Transfer": safeNum(r.english_yes),
    "Spa Transfer": safeNum(r.spanish_yes),
  })), [transfers]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Campaign
          </Typography>
          <Select value={campaign} onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}>
            {campaigns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && (
        <>
          {/* ── LANGUAGES ── */}
          <SectionTitle>Languages</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiCard label="Total records" value={langKpis.total.toLocaleString()}   color={BLUE}   delta={langKpis.totalDelta}      />
            <KpiCard label="English"       value={langKpis.english.toLocaleString()} color={BLUE}   delta={langKpis.englishDelta}    />
            <KpiCard label="English %"     value={langKpis.englishPct}               color={BLUE}   delta={langKpis.englishPctDelta} />
            <KpiCard label="Spanish %"     value={langKpis.spanishPct}               color={PURPLE} delta={langKpis.spanishPctDelta} />
          </Box>
          <ChartCard title="English vs Spanish" height={200}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={langChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="English" fill={BLUE}   radius={[3,3,0,0]} />
                <Bar dataKey="Spanish" fill={PURPLE} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── TRANSFERS ── */}
          <SectionTitle>Transfers</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiCard label="English records"       value={transferKpis.engRecords.toLocaleString()} color={BLUE} delta={transferKpis.engRecordsDelta} />
            <KpiCard label="English transfer rate" value={transferKpis.engRate}                     color={BLUE} delta={transferKpis.engRateDelta}    />
            <KpiCard label="Spanish records"       value={transferKpis.spaRecords.toLocaleString()} color={BLUE} delta={transferKpis.spaRecordsDelta} />
            <KpiCard label="Spanish transfer rate" value={transferKpis.spaRate}                     color={PINK} delta={transferKpis.spaRateDelta}    />
          </Box>
          <ChartCard title="English & Spanish transfer rate" height={200}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={transferChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Eng Transfer" fill={BLUE}   radius={[3,3,0,0]} />
                <Bar dataKey="Spa Transfer" fill={PURPLE} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </Box>
  );
}